// 🔒 PolitiqueConfidentialite - Page de politique de confidentialité RGPD
// Détails complets sur la collecte et traitement des données personnelles
// Utilisé dans : src/App.jsx (route "/privacy-policy")

import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import legalData from '@/locales/legal.json'

/**
 * Page politique de confidentialité conforme RGPD
 * Détaille la collecte, l'utilisation, la conservation et les droits des utilisateurs
 * Sections : Responsable, Données collectées, Finalités, Durée, Droits, Sécurité, Cookies
 */
export default function PolitiqueConfidentialite() {
  const { politiqueConfidentialite, association, contact, website, navigation, hosting } = legalData
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 transition-colors">
      <div className="container-custom px-4 py-24 max-w-4xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 mb-8 transition"
        >
          <ArrowLeft size={20} />
          {navigation.backToHome}
        </Link>

        <h1 className="text-4xl font-display font-bold text-primary-600 dark:text-primary-400 mb-8">
          {politiqueConfidentialite.title}
        </h1>

        <div className="bg-white dark:bg-neutral-800 rounded-2xl p-8 shadow-lg space-y-8 text-neutral-700 dark:text-neutral-300">
          
          {/* Introduction */}
          <section>
            <p>
              {politiqueConfidentialite.intro.paragraph1.replace('{associationName}', association.name).replace('{websiteUrl}', website.url)}
            </p>
            <p className="mt-4">
              {politiqueConfidentialite.intro.paragraph2}
            </p>
            <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-400">
              {politiqueConfidentialite.sections.lastUpdate.label} {politiqueConfidentialite.sections.lastUpdate.date}
            </p>
          </section>

          {/* Responsable du traitement */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Responsable du traitement des données
            </h2>
            <p>
              <strong>{association.name}</strong><br />
              {association.legalForm}<br />
              {association.address.street}<br />
              {association.address.postalCode} {association.address.city}<br />
              Email : {contact.email}
            </p>
          </section>

          {/* Données collectées */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              {politiqueConfidentialite.sections.dataCollected.title}
            </h2>
            <p className="mb-4">{politiqueConfidentialite.sections.dataCollected.intro}</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              {politiqueConfidentialite.sections.dataCollected.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Finalités */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Finalités du traitement
            </h2>
            <p className="mb-4">Vos données personnelles sont collectées pour les finalités suivantes :</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Assurer le bon fonctionnement du site web</li>
              <li>Améliorer l'expérience utilisateur</li>
              <li>Gérer les candidatures au casting Rock'In Agde</li>
              <li>Répondre à vos demandes de contact</li>
              <li>Vous informer sur l'événement (avec votre consentement)</li>
            </ul>
          </section>

          {/* Base légale */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Base légale du traitement
            </h2>
            <p>
              Le traitement de vos données personnelles repose sur :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
              <li>Votre consentement pour les cookies de préférence et les candidatures au casting</li>
              <li>L'intérêt légitime pour l'organisation et la promotion de l'événement</li>
              <li>L'exécution des mesures précontractuelles (traitement des candidatures)</li>
            </ul>
          </section>

          {/* Durée de conservation */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Durée de conservation
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Préférences utilisateur :</strong> Stockées localement dans votre navigateur jusqu'à suppression manuelle</li>
              <li><strong>Candidatures casting :</strong> Conservées 2 ans à partir de la date de dépôt</li>
              <li><strong>Demandes de contact :</strong> Conservées le temps nécessaire au traitement de votre demande (max 1 an)</li>
            </ul>
          </section>

          {/* Destinataires */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Destinataires des données
            </h2>
            <p className="mb-4">
              Vos données personnelles ne sont jamais vendues à des tiers. Elles peuvent être transmises uniquement :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Hébergeur du site :</strong> {hosting.name} - pour l'hébergement sécurisé
              </li>
              <li>
                <strong>Plateforme de formulaires :</strong> Tally.so - pour la gestion des candidatures casting
              </li>
              <li>
                <strong>Développeur :</strong> DOMELVIA (60 rue François 1er, 75008 Paris) - pour la maintenance technique
              </li>
              <li>Aux autorités compétentes sur demande légale</li>
            </ul>
          </section>

          {/* Vos droits */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Vos droits
            </h2>
            <p className="mb-4">
              Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi « Informatique et Libertés », 
              vous disposez des droits suivants :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Droit d'accès :</strong> Vous pouvez demander l'accès à vos données personnelles</li>
              <li><strong>Droit de rectification :</strong> Vous pouvez demander la correction de vos données inexactes</li>
              <li><strong>Droit à l'effacement :</strong> Vous pouvez demander la suppression de vos données</li>
              <li><strong>Droit d'opposition :</strong> Vous pouvez vous opposer au traitement de vos données</li>
              <li><strong>Droit à la portabilité :</strong> Vous pouvez récupérer vos données dans un format structuré</li>
              <li><strong>Droit de limitation :</strong> Vous pouvez demander la limitation du traitement</li>
            </ul>
            <p className="mt-4">
              Pour exercer ces droits, contactez-nous par email : <strong>{contact.email}</strong>
            </p>
            <p className="mt-4 text-sm">
              Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : 
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-secondary-600 dark:text-secondary-400 hover:underline ml-1">
                www.cnil.fr
              </a>
            </p>
          </section>

          {/* Sécurité */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Sécurité des données
            </h2>
            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles 
              contre la destruction accidentelle ou illicite, la perte accidentelle, l'altération, la diffusion ou l'accès non autorisés.
            </p>
            <p className="mt-4">
              Nos prestataires (hébergement, formulaires) sont sélectionnés pour leur conformité RGPD et leurs standards de sécurité.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Cookies et technologies similaires
            </h2>
            <p className="mb-4">
              Ce site utilise uniquement des cookies techniques strictement nécessaires au fonctionnement :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>localStorage :</strong> Sauvegarde de vos préférences (mode clair/sombre)</li>
              <li><strong>Pas de cookies tiers :</strong> Aucun cookie de traçage, publicité ou réseaux sociaux</li>
              <li><strong>Pas de consentement requis :</strong> Cookies purement techniques exemptés selon le RGPD</li>
            </ul>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Modifications de la politique
            </h2>
            <p>
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. 
              Toute modification sera publiée sur cette page avec une date de mise à jour.
            </p>
            <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-400">
              <strong>Dernière mise à jour :</strong> {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </section>

          {/* Contact */}
          <section className="border-t border-neutral-200 dark:border-neutral-700 pt-8">
            <h2 className="text-2xl font-display font-semibold text-primary-600 dark:text-primary-400 mb-4">
              Nous contacter
            </h2>
            <p className="mb-4">
              Pour toute question concernant cette politique de confidentialité ou l'utilisation de vos données personnelles :
            </p>
            <div className="bg-primary-50 dark:bg-neutral-700/50 rounded-lg p-6">
              <p className="font-semibold mb-2">{association.name}</p>
              <p className="text-sm space-y-1">
                {association.address.street}<br />
                {association.address.postalCode} {association.address.city}<br />
                Email : <a href={`mailto:${contact.email}`} className="text-primary-600 dark:text-primary-400 hover:underline">{contact.email}</a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  )
}
